import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  TextField,
  Typography,
  Button,
  Container,
} from "@mui/material";

const CompanyInfo = ({ csrfToken }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const res = await fetch("/api/company_info");
        if (!res.ok) throw new Error("会社情報の取得に失敗しました");
        const data = await res.json();
        setCompany(data);
        setDescription(data.business_description || "");
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const telParts = useMemo(() => {
    const raw = (company?.phone_number || "").trim();
    if (!raw) return ["", "", ""];

    // 既にハイフン区切りならそのまま（3分割に正規化）
    if (raw.includes("-")) {
      const parts = raw.split("-").slice(0, 3);
      return [parts[0] || "", parts[1] || "", parts[2] || ""];
    }

    // 数字以外を除去して自動整形
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 11) {
      return [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7, 11)];
    }
    if (digits.length === 10) {
      return [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6, 10)];
    }

    // 想定外の長さは左詰めで表示
    return [digits.slice(0, 3) || "", digits.slice(3, 7) || "", digits.slice(7, 11) || ""];
  }, [company?.phone_number]);

  const handleUpdate = async () => {
    try {
      const res = await fetch("/api/company_info", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ company: { business_description: description } }),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.errors?.join("\n") || "更新に失敗しました");
      }
      toast.success("更新しました。");
    } catch (e) {
      toast.error(e.message || "更新に失敗しました");
    }
  };

  const sharedTextFieldStyle = {
    "& .MuiFilledInput-root": {
      border: "1px solid #000",
      backgroundColor: "#fff",
      py: "19px",
      pl: "22px",
      fontSize: "14px",
      borderRadius: 0,
      "&:focus": {
        outline: "none",
      },
      "&:hover": {
        backgroundColor: "#fff",
      },
      "&.Mui-forcused": {
        backgroundColor: "#fff",
      },
      "&.MuiFilledInput-root": {
      backgroundColor: "#fff",
      },
    },
    "& .MuiFilledInput-input": {
      p: 0,
    },
    "& .MuiFilledInput-root:focas-whithin": {
      outline: "none",
      boxShadow: "none",
    },
    "& .MuiInputBase-root.Mui-forcused": {
    boxShadow: "none !important",
    },
    "& input": {
      fontSize: "14px",
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:forcus-within": {
      boxShadow: "none !important",
      outline: "none !important",
    },
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ pt: 3, pb: 0 }}>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ pt: 3, pb: 0 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!company) return null;

  return (
    <Container maxWidth="md" sx={{ pt: 3, pb: 0, display: "flex", flexDirection: "column" }}>
      <Typography variant="h5" mb={4} fontWeight="bold">
        会社情報
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {/* 会社名 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{fontSize: "14px"}}>
            会社名
          </Typography>
          <TextField
            value={company.name || ""}
            fullWidth
            variant="filled"
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={sharedTextFieldStyle}
          />
        </Box>

        {/* 事業内容 */}
        <Box>
          <Typography sx={{fontSize: "14px"}}>
            事業内容（最大50文字まで）
          </Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            variant="filled"
            inputProps={{ maxLength: 50 }}
            InputProps={{ readOnly: false, disableUnderline: true }}
            sx={sharedTextFieldStyle}
          />
          <Typography
            sx={{
              textAlign: "right",
              mt: "4px",
              fontSize: "12px",
              color: "#767676",
            }}
          >
            {`${(description ?? "").length ?? 0} 文字`}
          </Typography>
        </Box>

        {/* 所在地 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{fontSize: "14px"}}>
            所在地
          </Typography>
          <TextField
            value={company.address || ""}
            fullWidth
            variant="filled"
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={sharedTextFieldStyle}
          />
        </Box>

        {/* メールアドレス */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{fontSize: "14px"}}>
            メールアドレス
          </Typography>
          <TextField
            value={company.email || ""}
            fullWidth
            variant="filled"
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={sharedTextFieldStyle}
          />
        </Box>

        {/* 電話番号 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{fontSize: "14px"}}>
            電話番号
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {telParts.map((part, i) => (
              <React.Fragment key={i}>
                  <TextField
                    value={part}
                    variant="filled"
                    InputProps={{ readOnly: true, disableUnderline: true }}
                    sx={{
                      ...sharedTextFieldStyle,
                      width: "74px",
                    }}
                  />
                  {i < telParts.length - 1 && (
                    <Typography sx={{ mx: 1 }}>-</Typography>
                  )}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* 更新ボタン */}
        <Box mt={1}>
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{
              px: "22px",
              py: "8px",
              borderRadius: 0,
              backgroundColor: "#000",
              "&:hover": {
                backgroundColor: "#333",
              }
            }}
          >
            更新
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CompanyInfo;